/**
 * `<caos-status-pill>` — a tinted state pill on a record or configuration row.
 * (Catalog: Status & feedback › StatusPill — a tinted state pill on a record or configuration row.)
 *
 * A FAITHFUL PORT of the reference application's status pill (citing `crm/objectFramework.tsx`): a
 * ROUNDED (border-radius 999px), TRANSPARENT-background span with the intent's tint applied
 * to BOTH the text colour and a 1px border — 9px mono, 0.14em tracking, uppercase, 2px/8px
 * padding. The color is the message; there is no filled chip. (The earlier square filled-chip
 * "§04" styling was invented rather than ported, and is replaced here.)
 *
 * Intents map to the Dragon tint vocabulary: info → `--warn`-adjacent accent (In Process),
 * success → `--ok` (Won), warning → `--warn` (Open / Due), danger → `--red` (Expired / Lost),
 * muted → `--ink-muted` (Draft / neutral), dev → accent-strong (DEV). An optional `dot`
 * attribute prefixes a 6px status dot in the pill's own colour. The label is the default slot;
 * `::part(pill)` and `::part(dot)` are the styling seams.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

export type CaosStatusIntent = 'info' | 'success' | 'warning' | 'danger' | 'muted' | 'dev';

const sheet = makeSheet(`
  :host { display: inline-flex; vertical-align: middle; }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid currentColor;
    background: transparent;
    font-family: var(--caos-font-mono);
    font-size: 9px;
    font-weight: var(--caos-font-weight-label);
    letter-spacing: 0.14em;
    text-transform: uppercase;
    line-height: 1.6;
    white-space: nowrap;
    /* default (muted / Draft) — tint on text + border, transparent fill (Dragon StatusPill). */
    color: var(--caos-color-text-muted);
  }

  /* optional 6px status dot in the pill's own (current) colour — Dragon .chip-dot */
  .dot {
    display: none;
    flex: none;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  :host([dot]) .dot { display: block; }

  /* Each intent tints TEXT (and, via currentColor, the 1px border) — no filled background. */
  :host([intent="info"]) .pill { color: var(--caos-color-info); }
  :host([intent="success"]) .pill { color: var(--caos-color-success); }
  :host([intent="warning"]) .pill { color: var(--caos-color-warning); }
  :host([intent="danger"]) .pill { color: var(--caos-color-danger); }
  :host([intent="muted"]) .pill { color: var(--caos-color-text-muted); }
  :host([intent="dev"]) .pill { color: var(--caos-color-accent-strong); }
`);

export class CaosStatusPill extends CaosElement {
  constructor() {
    super(sheet);
    const pill = document.createElement('span');
    pill.className = 'pill';
    pill.setAttribute('part', 'pill');
    const dot = document.createElement('span');
    dot.className = 'dot';
    dot.setAttribute('part', 'dot');
    dot.setAttribute('aria-hidden', 'true');
    pill.append(dot, document.createElement('slot'));
    this.root.append(pill);
  }
}

define('caos-status-pill', CaosStatusPill);

declare global {
  interface HTMLElementTagNameMap {
    'caos-status-pill': CaosStatusPill;
  }
}
